/**
 * @fileoverview Confirmation Modal Component
 * 
 * This file contains a reusable confirmation modal component for the Heidrick & Struggles
 * Leader Profile application. The modal provides a consistent way to ask for user
 * confirmation before performing important actions.
 * 
 * Features:
 * - Customizable header and body text
 * - Optional cancel button
 * - Loading state for the continue button
 * - Standardized styling consistent with application design
 */

import React from 'react';
import { Modal, ModalSize, Button, ButtonVariant, IconName, ButtonIconAlign} from '@eightfold.ai/octuple';
import { actionButtonStyle, secondaryButtonStyle } from './commonStyles';
import InfoBanner from './InfoBanner';
import { formatRaterGroupName } from './commonFunctions';

/**
 * Modal Body Component
 * 
 * Renders the content of the confirmation modal including message text
 * and action buttons (Cancel and Continue).
 * 
 * @param {Object} props - Component props
 * @param {string} props.bodyText - The message text to display in the modal
 * @param {boolean} props.showCancelButton - Whether to show the Cancel button
 * @param {boolean} props.continueButtonLoader - Whether to show a loading spinner on the Continue button
 * @param {Function} props.onClose - Function to call when the modal is closed/cancelled
 * @param {Function} props.onContinue - Function to call when the Continue button is clicked
 * @returns {JSX.Element} The modal body content
 */
const ModalBody = (props: any) => {

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {props?.ratersSummary ? (
          <div style={{display: 'flex', flexDirection: 'column'}}>
            <InfoBanner 
              message="You haven't nominated the recommended minimum number of raters"
            />
              <div className="ratersSummaryContainer">
                {Object.keys(props?.ratersSummary).map((raterType, index, array) => (
                <React.Fragment key={raterType}>
                  <div className="raterItem">
                    {props.ratersSummary[raterType].filled < props.ratersSummary[raterType].minimum ? (
                      <img 
                        src='./../assets/img/Alert.svg'
                        alt="Alert Icon"
                      />
                    ) : (
                      <div style={{ width: '24px', minWidth: '24px' }}></div>
                    )}
                    <div className="raterContent">
                      <span className="raterTypeLabel">{formatRaterGroupName(raterType)}</span>
                      <span className="raterCount">{props.ratersSummary[raterType].filled}/{props.ratersSummary[raterType].minimum}</span>
                    </div>
                  
                  </div> 
                  {index < array.length - 1 && <hr className='listItemSeparator' style={{border:'1px solid #E0E1E5'}}/>}                 
                  </React.Fragment>
                  
                ))}
              </div>
          
          </div>
        )
        :
        <div style={{ paddingBottom: '20px', color: '#69717F' }}>{props.bodyText}</div>
      }
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px' }}>
        {/* Conditional rendering of the Cancel button */}
        {props?.showCancelButton ? (
          <>
            <Button
              text={props?.cancelBtnTxt || 'Cancel'}
              variant={ButtonVariant.Secondary}
              classNames={props?.ratersSummary ? 'navReturnButton' :'secondaryButton'}
              style={secondaryButtonStyle}
              onClick={() => {
                props?.onClose();
              }}
              transparent={true}
              iconProps={props?.cancelButtonLoader ? { path: IconName.mdiLoading, spin: true } : undefined }
              alignIcon={ButtonIconAlign.Right}
              disabled={props?.cancelButtonLoader}
            />
          </>
        ) : (
          ''
        )}
        {/* Continue button - always displayed */}
        <Button
          text={props?.continueBtnTxt || 'Continue'}
          variant={ButtonVariant.Secondary}
          classNames='actionButton'
          style={actionButtonStyle}
          onClick={() => {
            props?.onContinue();
          }}
          iconProps={props?.continueButtonLoader ? { path: IconName.mdiLoading, spin: true } : { path: IconName.mdiArrowRight }}
          alignIcon={ButtonIconAlign.Right}
          disabled={props?.continueButtonLoader}
        />
      </div>
    </div>
  );
};

/**
 * Confirmation Modal Component
 * 
 * A reusable modal dialog that prompts users to confirm or cancel an action.
 * This component encapsulates the Modal from Octuple UI library and provides
 * a simplified interface for showing confirmation dialogs throughout the application.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.showModal - Whether the modal is visible
 * @param {string} props.header - The header text for the modal
 * @param {string} props.bodyText - The message text to display in the modal body
 * @param {boolean} props.showCancelButton - Whether to show a Cancel button (optional)
 * @param {boolean} props.continueButtonLoader - Whether the Continue button should show a loading state
 * @param {Function} props.onClose - Function to call when the modal is closed or cancelled
 * @param {Function} props.onContinue - Function to call when the Continue button is clicked
 * @returns {JSX.Element} The confirmation modal component
 */
const ConfirmationModal = (props: any) => {
  /**
   * Handles the continue button click
   * Calls the onContinue callback provided in props
   */
  const onContinue = () => {
    props.onContinue();
  };

  /**
   * Handles the close/cancel action
   * Calls the onClose callback provided in props
   */
  const onClose = () => {
    props.onClose();
  };

  const onCloseTop = () => {
    props.onCloseTop();
  };

  return (
    <Modal
      visible={props?.showModal}
      actionsClassNames='my-modal-actions-class'
      body={
        <ModalBody
          continueButtonLoader={props?.continueButtonLoader}
          showCancelButton={props?.showCancelButton}
          onContinue={onContinue}
          onClose={onClose}
          bodyText={props?.bodyText}
          color={props?.color}
          ratersSummary={props?.ratersSummary}
          continueBtnTxt={props?.continueBtnTxt}
          cancelBtnTxt={props?.cancelBtnTxt}
          cancelButtonLoader={props?.cancelButtonLoader}
        />
      }
      bodyClassNames='my-modal-body-class'
      bodyPadding
      configContextProps={{
        noGradientContext: false,
        noThemeContext: false,
      }}
      data-test-id='my-modal-test-id'
      header={props.header}
      headerClassNames='my-modal-header-class'
      modalClassNames='my-modal-class'
      modalWrapperClassNames='my-modal-wrapper-class'
      onClose={(props?.isInviteRatersModal) ? onCloseTop :onClose}
      overlay
      positionStrategy='absolute'
      renderContentAlways
      size={ModalSize.medium}
      themeContainerId='my-modal-theme-container'
      zIndex={1000}
      closable={props?.isAcknowledge ? false : true}
      maskClosable={props?.isAcknowledge ? false : true}
    />
  );
};

export default ConfirmationModal;
